import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Zap } from 'lucide-react'

import { EID } from '@/lib/element-ids.registry'

import { AnalysisModeCards, type AnalysisMode } from '@/components/v2/tools/amazon-listing/CampaignCreator/AnalysisModeCards'

import { ListingAsinTab } from './ListingAsinTab'
import { ListingChannelTab } from './ListingChannelTab'
import { ListingCreditHint } from './ListingCreditHint'
import { ListingManualTab } from './ListingManualTab'
import type { SourceSubmitStatus } from './sourceSubmit'

type SourceTab = 'asin' | 'channel' | 'manual'

interface ListingSourceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onStarted: (jobId: string) => void
}

const TAB_IDS: Record<SourceTab, string> = {
  asin: EID['listing-builder-source-tab-asin'],
  channel: EID['listing-builder-source-tab-channel'],
  manual: EID['listing-builder-source-tab-manual'],
}

export function ListingSourceDialog({ open, onOpenChange, onStarted }: ListingSourceDialogProps) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<SourceTab>('asin')
  const [mode, setMode] = useState<AnalysisMode>('standard')
  const submitRef = useRef<(() => void) | null>(null)
  const [footerStatus, setFooterStatus] = useState<SourceSubmitStatus>({
    canSubmit: false,
    isPending: false,
    submitId: EID['listing-builder-asin-submit-btn'],
    labelKey: 'listingBuilder.source.start',
  })
  const registerSubmit = useCallback((fn: () => void) => {
    submitRef.current = fn
  }, [])

  if (!open) return null

  const close = () => onOpenChange(false)
  const handleStarted = (jobId: string) => {
    close()
    onStarted(jobId)
  }
  const handleBatchStarted = () => close()

  const tabCls = (active: boolean) =>
    `px-4 py-2 text-sm font-medium transition-colors ${active ? 'text-orange-500 border-b-2 border-orange-500' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true"
      id={EID['listing-builder-source-dialog']}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={close} />
      <div className="relative mx-4 flex max-h-[98vh] w-full max-w-lg flex-col rounded-2xl bg-white p-6 shadow-2xl dark:bg-neutral-800">
        <button onClick={close}
          className="absolute top-4 right-4 p-1 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
          aria-label={t('common.close')}>
          <X className="h-5 w-5 text-neutral-500" />
        </button>

        <h2 className="shrink-0 text-xl font-bold text-neutral-900 dark:text-white mb-4">
          {t('listingBuilder.source.title')}
        </h2>

        <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex border-b border-neutral-200 dark:border-neutral-700 mb-5" role="tablist">
          {(['asin', 'channel', 'manual'] as const).map((tab) => (
            <button key={tab} id={TAB_IDS[tab]} role="tab" aria-selected={activeTab === tab}
              aria-controls={`${TAB_IDS[tab]}-panel`}
              onClick={() => setActiveTab(tab)} className={tabCls(activeTab === tab)}>
              {t(`listingBuilder.source.tabs.${tab}`)}
            </button>
          ))}
        </div>

        <div role="tabpanel" id={`${TAB_IDS[activeTab]}-panel`} aria-labelledby={TAB_IDS[activeTab]}>
          {activeTab === 'asin' && (
            <ListingAsinTab onStarted={handleStarted} onBatchStarted={handleBatchStarted} mode={mode}
              registerSubmit={registerSubmit} onStatusChange={setFooterStatus} />
          )}
          {activeTab === 'channel' && (
            <ListingChannelTab onStarted={handleStarted} mode={mode}
              registerSubmit={registerSubmit} onStatusChange={setFooterStatus} />
          )}
          {activeTab === 'manual' && (
            <ListingManualTab onStarted={handleStarted} mode={mode}
              registerSubmit={registerSubmit} onStatusChange={setFooterStatus} />
          )}
        </div>

        <div className="mt-5">
          <AnalysisModeCards value={mode} onChange={setMode} />
        </div>

        <div className="mt-5 border-t border-neutral-200 dark:border-neutral-700 pt-3">
          <ListingCreditHint />
        </div>
        </div>

        <button
          id={footerStatus.submitId}
          onClick={() => submitRef.current?.()}
          disabled={!footerStatus.canSubmit}
          className="mt-5 flex w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-orange-500 px-5 py-3 font-medium text-white transition-colors hover:bg-orange-600 disabled:bg-neutral-300 dark:disabled:bg-neutral-600"
        >
          <Zap className="h-4 w-4" />
          {footerStatus.isPending ? t('common.loading') : t(footerStatus.labelKey)}
        </button>
      </div>
    </div>
  )
}
